/**
 * Unified Join Page
 *
 * Single entry for /join/:token per Hierarchical Join Link System plan.
 * Resolves token via GroupJoinService and renders the appropriate flow in place:
 * designation → AcceptDesignation, invitation → InvitationAcceptPage, invite code → JoinOrganization,
 * template → AcceptTemplate (ChartMind Patient Encounter Template).
 *
 * Role-aware for template tokens:
 *   - patient → redirected to /checkin/:clinicId/:templateId (check-in flow)
 *   - professional-chartmind → AcceptTemplate (links template to user's account)
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { resolveToken, JOIN_TOKEN_TYPE } from '@healthdesk/shared-services';
import { useAuth } from '@healthdesk/shared-hooks';
import JoinOrganization from '../JoinOrganization';
import { AcceptDesignation } from '../staff';
import InvitationAcceptPage from '../auth/InvitationAcceptPage';
import AcceptTemplate from '../../features/chartmind/components/chartmind/AcceptTemplate';
import AcceptRegionalTemplate from '../../features/chartmind/components/chartmind/AcceptRegionalTemplate';
import { chartmindAdminService } from '../../services/chartmind';

export default function UnifiedJoinPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { userData, loading: authLoading } = useAuth();
  const [resolution, setResolution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!token?.trim()) {
        setError('Invalid link');
        setLoading(false);
        return;
      }
      try {
        const result = await resolveToken(token.trim());
        if (cancelled) return;
        setResolution(result);
      } catch (err) {
        console.error('[UnifiedJoinPage] resolveToken error:', err);
        if (!cancelled) setError('Could not resolve link');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token]);

  // Patient + template token → redirect to check-in flow
  useEffect(() => {
    if (
      authLoading ||
      !resolution ||
      resolution.type !== JOIN_TOKEN_TYPE.TEMPLATE ||
      userData?.role !== 'patient'
    ) {
      return;
    }

    let cancelled = false;
    async function redirectToCheckIn() {
      try {
        const result = await chartmindAdminService.findTemplateByInviteCode(resolution.payload.code);
        if (cancelled) return;
        if (!result) {
          setError('Patient Encounter Template not found.');
          return;
        }
        const link = result.template?.checkInLink;
        if (link?.clinicId && link?.templateId) {
          navigate(
            `/checkin/${encodeURIComponent(link.clinicId)}/${encodeURIComponent(link.templateId)}`,
            { replace: true }
          );
        } else {
          setError('This clinic has not configured a check-in process yet. Please check in at the front desk.');
        }
      } catch (err) {
        console.error('[UnifiedJoinPage] Patient check-in redirect error:', err);
        if (!cancelled) setError('Could not load check-in information.');
      }
    }
    redirectToCheckIn();
    return () => { cancelled = true; };
  }, [authLoading, userData?.role, resolution, navigate]);

  if (loading || authLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Resolving link…</Typography>
      </Box>
    );
  }

  // If patient + template, show loading while redirect is in progress
  if (
    resolution?.type === JOIN_TOKEN_TYPE.TEMPLATE &&
    userData?.role === 'patient' &&
    !error
  ) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Preparing check-in…</Typography>
      </Box>
    );
  }

  if (error || !resolution) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
        <Typography color="error">{error || 'This link is invalid or has expired.'}</Typography>
      </Box>
    );
  }

  switch (resolution.type) {
    case JOIN_TOKEN_TYPE.DESIGNATION:
      return <AcceptDesignation designationId={resolution.payload.designationId} />;
    case JOIN_TOKEN_TYPE.INVITATION:
      return <InvitationAcceptPage invitationId={resolution.payload.invitationId} />;
    case JOIN_TOKEN_TYPE.INVITE_CODE:
      return <JoinOrganization codeFromToken={resolution.payload.code} />;
    case JOIN_TOKEN_TYPE.TEMPLATE:
      return <AcceptTemplate templateCode={resolution.payload.code} />;
    case JOIN_TOKEN_TYPE.REGIONAL_TEMPLATE:
      return <AcceptRegionalTemplate templateCode={resolution.payload.code} />;
    default:
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" p={3}>
          <Typography color="error">Unknown link type.</Typography>
        </Box>
      );
  }
}
